import { productBuilder } from '@noths/polaris-client-data-builders';
import { PROMISE_SUPERSEDED_ERROR_MESSAGE } from '@noths/polaris-client-utils';
import { createSlice } from '@reduxjs/toolkit';

import { categoryBuilder } from 'src/builders/category';
import { logger } from 'src/utils';
import { addLoadingProducts } from './actions';
import { requestFeaturedProduct, requestProducts, updateFilters } from './thunks';
import { ProductsState } from './types';

const PRODUCTS_PER_PAGE = 60;

export const initialState: Readonly<ProductsState> = {
  searchTerm: '',
  category: categoryBuilder(),
  currencyCode: 'GBP',
  items: {},
  currentPage: 0,
  totalPages: 0,
  totalProducts: 0,
  lastRequestedPageNumber: 0,
  productsReturnedCount: 0,
  queryType: '',
  navigation: [],
  isLoading: false,
  isUpdateFiltersLoading: false,
  featuredProduct: null,
  providerAttributionToken: null,
  zeroExactResults: null,
  pinnedResultCount: null,
};

export const { actions, reducer } = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestProducts.fulfilled, (state, { meta, payload }) => {
        const {
          replaceProducts = true,
          query: { page },
        } = meta.arg;

        const lastRequestedPageNumber = Number(page ?? 1);

        if (payload.data) {
          const items = replaceProducts
            ? { ...payload.data.items }
            : {
                ...state.items,
                ...payload.data.items,
              };

          const {
            providerAttributionToken,
            currencyCode,
            productsReturnedCount,
            queryType,
            currentPage,
            totalPages,
            totalProducts,
            category,
            partner,
            navigation,
            searchTerm = state.searchTerm,
            zeroExactResults = null,
            pinnedResultCount = null,
          } = payload.data;

          return {
            ...state,
            currencyCode,
            searchTerm,
            productsReturnedCount,
            queryType,
            currentPage:
              replaceProducts || currentPage > state.currentPage ? currentPage : state.currentPage,
            totalPages,
            totalProducts,
            lastRequestedPageNumber,
            category,
            partner,
            navigation,
            items,
            isLoading: false,
            error: null,
            providerAttributionToken,
            zeroExactResults,
            pinnedResultCount,
          } as ProductsState;
        }

        if (payload.redirectUrl) {
          state.redirectUrl = payload.redirectUrl;
          return;
        }

        return state;
      })

      .addCase(requestProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestProducts.rejected, (state, action) => {
        if (action.error && action.error.message === PROMISE_SUPERSEDED_ERROR_MESSAGE) {
          return;
        }

        logger.error('Error requesting products', action.error);
        state.items = {};
        state.error = action.error;
        state.isLoading = false;
      })
      .addCase(updateFilters.pending, (state) => {
        state.isUpdateFiltersLoading = true;
      })
      .addCase(updateFilters.fulfilled, (state) => {
        state.isUpdateFiltersLoading = false;
      })
      .addCase(addLoadingProducts, (state, { payload: page }) => {
        state.items[Number(page)] = Array.from({ length: PRODUCTS_PER_PAGE }, (_, index) =>
          productBuilder({
            code: index,
            isLoading: true,
          }),
        );
      })
      .addCase(requestFeaturedProduct.fulfilled, (state, { payload }) => {
        state.featuredProduct = payload;
      })
      .addCase(requestFeaturedProduct.rejected, (_, action) => {
        logger.error('Error requesting featured product', action.error);
      });
  },
});
